import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import { Page } from 'Common/Page/Page'
import { PageHeader } from 'Common/Supplyvue-ui'
import { useState, ChangeEvent, useEffect, useRef, useReducer } from 'react'
import { PlanDataTypes } from 'Features/OperationalPlanner/OperationalPlanner.types'
import {
  planDataReducer,
  PLAN_DATA_ACTIONS,
} from 'Features/OperationalPlanner/CreateOperationalPlan/planDataReducer'
import { VariantSelectorSimple } from 'Features/OperationalPlanner/CreateOperationalPlan/shared/components'
import { PlanStartWeekInput } from 'Features/OperationalPlanner/CreateOperationalPlan/PlanStartWeekInput'
import { PlanDescriptionInput } from 'Features/OperationalPlanner/CreateOperationalPlan/PlanDescriptionInput'
import { PlanHorizonInputs } from 'Features/OperationalPlanner/CreateOperationalPlan/PlanHorizonInputs'
import { ConstrainingHorizonInputs } from 'Features/OperationalPlanner/CreateOperationalPlan/ConstrainingHorizonInputs'
import { PlanAccessSelector } from 'Features/OperationalPlanner/CreateOperationalPlan/PlanAccessSelector'
import { PlanActionButtons } from 'Features/OperationalPlanner/CreateOperationalPlan/PlanActionButtons'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useSourceSites } from 'Features/OperationalPlanner/OperationalPlan.data'
import { useCreateOperationalPlanV2 } from './OperationalPlanV2.data'
import { WorkcentreModal } from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.view'
import { SourceSiteDetails } from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.types'
import { Loader } from 'Common/Loader/Loader'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import { useUserSettings } from 'Features/OperationalPlanner/UserSettings.data'
import { getUserEmail } from 'Features/Auth/auth'
import { ConstrainingHorizonsModal } from 'Features/OperationalPlanner/CreateOperationalPlan/ConstrainingHorizonsModal/ConstrainingHorizonsModal'
import {
  isFormValid,
  processSelectedSourceSites,
  preparePlanDataForSubmission,
  createDefaultPlanData,
  flattenWorkcentresForTable,
  unflattenWorkcentresFromTable,
  WorkcentreTableRow,
} from 'Features/OperationalPlanner/CreateOperationalPlan/utils/planHelpers'

const tenant = localStorage.getItem('tenantId') ?? ''

const createPlanData = createDefaultPlanData()

const horizonSelectionMode = { weeks: 'weeks', date: 'date' }

export const CreateOperationalPlanV2Page = () => {
  useBreadcrumbsData({
    plan_id: '',
  })
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const newlyCreatedVariantName = useRef<string | null>(null)
  const [createPlanDataObject, dispatch] = useReducer(
    planDataReducer,
    createPlanData
  )
  const navigate = useNavigate()

  const handleActionClick = () => {
    setIsModalOpen(true)
  }

  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(
    null
  )
  const [horizonsSelectionModeState, setHorizonsSelectionModeState] = useState({
    planHorizon: horizonSelectionMode.weeks,
    constrainingHorizon: horizonSelectionMode.weeks,
  })

  const { data: sourceSiteData, isLoading: isSourceSitesLoading } =
    useSourceSites(tenant)

  const [isConstrainingHorizonsModalOpen, setIsConstrainingHorizonsModalOpen] =
    useState<boolean>(false)

  useEffect(() => {
    if (userEmail === null) {
      getUserEmail().then((email: string | undefined) => {
        setUserEmail(email ? email.toLocaleLowerCase() : '')
      })
    }
    return () => {
      setUserEmail(null)
    }
  }, [])

  const { data: userSettingsData } = useUserSettings(userEmail as string)

  const handleManageVariants = () => {
    navigate('/create-plan-v2/manage-variants')
  }

  const handleEditSettings = () => {
    if (!isFormValid(createPlanDataObject)) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      SetIsEditSettings(true)
      const planData = preparePlanDataForSubmission(createPlanDataObject, false)
      submitCreatePlan(planData)
    } catch (error) {
      SetIsEditSettings(false)
      toast.error('Error preparing plan data')
    }
  }

  const handleRunPlan = () => {
    if (!isFormValid(createPlanDataObject)) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      SetIsRunPlan(true)
      const planData = preparePlanDataForSubmission(createPlanDataObject, true)
      submitCreatePlan(planData)
    } catch (error) {
      SetIsRunPlan(false)
      toast.error('Error preparing plan data')
    }
  }

  const submitCreatePlan = (planData: PlanDataTypes) => {
    createOperationalPlanV2(planData)
  }

  const [isRunPlan, SetIsRunPlan] = useState<boolean>(false)
  const [isEditSettings, SetIsEditSettings] = useState<boolean>(false)

  const onSuccessfulPlanCreation = (
    planId: string,
    unconstrainedPlan: boolean
  ) => {
    toast.success('Plan created successfully')
    SetIsRunPlan(false)
    SetIsEditSettings(false)

    if (unconstrainedPlan) {
      navigate(`/`)
    } else {
      navigate(`/plan/${planId}/edit`)
    }
  }

  const onFailedPlanCreation = () => {
    SetIsRunPlan(false)
    SetIsEditSettings(false)
    toast.error('Plan creation failed, Please retry.')
  }

  const { mutate: createOperationalPlanV2, isLoading: isPlanCreating } =
    useCreateOperationalPlanV2({
      onSuccessfulPlanCreation,
      onFailedPlanCreation,
    })

  const getSelectedSites = (sourceSiteData: SourceSiteDetails[]) => {
    const selectedSourceSites = processSelectedSourceSites(
      sourceSiteData,
      createPlanDataObject.constrainingHorizon
    )
    dispatch({
      type: PLAN_DATA_ACTIONS.SET_SOURCE_SITES,
      payload: selectedSourceSites,
    })
    setIsModalOpen(false)
  }

  const handleAccessTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    dispatch({
      type: PLAN_DATA_ACTIONS.SET_PLAN_ACCESS_TYPE,
      payload: value,
    })
  }

  const handleUpdateConstrainingHorizons = () => {
    setIsConstrainingHorizonsModalOpen(true)
  }

  const handleSaveConstrainingHorizons = (
    updatedData: WorkcentreTableRow[]
  ) => {
    const updatedSourceSites = unflattenWorkcentresFromTable(
      updatedData,
      createPlanDataObject.sourceSites
    )

    dispatch({
      type: PLAN_DATA_ACTIONS.UPDATE_WORKCENTRE_CONSTRAINING_DATA,
      payload: updatedSourceSites,
    })

    setIsConstrainingHorizonsModalOpen(false)
  }

  if (isSourceSitesLoading) {
    return <Loader />
  }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Create operational plans" />
        <Box display="flex" flexDirection="column" flex={1} alignItems="center">
          <Box width="40%">
            <Box sx={{ minWidth: '400px' }}>
              <Box display="flex" flexDirection="column">
                <VariantSelectorSimple
                  userSettingsData={userSettingsData}
                  selectedTemplateKey={selectedTemplateKey}
                  setSelectedTemplateKey={setSelectedTemplateKey}
                  dispatch={dispatch}
                  createPlanData={createPlanData}
                  setHorizonsSelectionModeState={setHorizonsSelectionModeState}
                  horizonsSelectionModeState={horizonsSelectionModeState}
                  horizonSelectionMode={horizonSelectionMode}
                  newlyCreatedVariantName={newlyCreatedVariantName}
                  onManageVariants={handleManageVariants}
                />
                <PlanStartWeekInput
                  createPlanDataObject={createPlanDataObject}
                  dispatch={dispatch}
                  isPlanCreating={isPlanCreating}
                  horizonsSelectionModeState={horizonsSelectionModeState}
                  setIsSelectedVariantEdited={() => {}}
                  selectedTemplateKey={selectedTemplateKey}
                />

                <PlanDescriptionInput
                  createPlanDataObject={createPlanDataObject}
                  dispatch={dispatch}
                  isPlanCreating={isPlanCreating}
                />

                <PlanHorizonInputs
                  createPlanDataObject={createPlanDataObject}
                  createPlanData={createPlanData}
                  dispatch={dispatch}
                  isPlanCreating={isPlanCreating}
                  horizonsSelectionModeState={horizonsSelectionModeState}
                  setHorizonsSelectionModeState={setHorizonsSelectionModeState}
                  horizonSelectionMode={horizonSelectionMode}
                  selectedTemplateKey={selectedTemplateKey}
                  setIsSelectedVariantEdited={() => {}}
                />

                <ConstrainingHorizonInputs
                  createPlanDataObject={createPlanDataObject}
                  createPlanData={createPlanData}
                  dispatch={dispatch}
                  isPlanCreating={isPlanCreating}
                  horizonsSelectionModeState={horizonsSelectionModeState}
                  setHorizonsSelectionModeState={setHorizonsSelectionModeState}
                  horizonSelectionMode={horizonSelectionMode}
                  selectedTemplateKey={selectedTemplateKey}
                  setIsSelectedVariantEdited={() => {}}
                />

                <PlanAccessSelector
                  value={createPlanDataObject.planAccessType}
                  onChange={handleAccessTypeChange}
                  disabled={isPlanCreating}
                />
              </Box>
            </Box>
            <PlanActionButtons
              createPlanDataObject={createPlanDataObject}
              isPlanCreating={isPlanCreating}
              isRunPlan={isRunPlan}
              isEditSettings={isEditSettings}
              onSelectSourceSites={handleActionClick}
              onRunPlan={handleRunPlan}
              onEditSettings={handleEditSettings}
              onUpdateConstrainingHorizons={handleUpdateConstrainingHorizons}
            />
          </Box>
        </Box>
      </Box>
      {isModalOpen && (
        <WorkcentreModal
          isModalOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
          handleOkbuttonClick={getSelectedSites}
          isUpdating={false}
          readOnly={false}
          sourceSiteData={sourceSiteData ?? []}
          templateSourceSiteDetails={createPlanDataObject.sourceSites ?? []}
        />
      )}
      {isConstrainingHorizonsModalOpen && (
        <ConstrainingHorizonsModal
          isOpen={isConstrainingHorizonsModalOpen}
          onClose={() => setIsConstrainingHorizonsModalOpen(false)}
          onSave={handleSaveConstrainingHorizons}
          tableData={flattenWorkcentresForTable(createPlanDataObject)}
          planStartWeek={createPlanDataObject.planStartWeek as Date}
          isLoading={false}
        />
      )}
    </Page>
  )
}
